/**
 * Plausibilità delle specifiche di un simbolo.
 *
 * Questi numeri si scrivono a mano nella pagina Server, e da lì entrano nel
 * motore: `valuePerPoint` decide i punti di SL e TP, `lotStep` decide i lotti.
 * Un valore sbagliato non rompe niente — sbaglia tutto, in silenzio. Nel
 * database di sviluppo un server broker ha `valuePerPoint` 0,01 dove gli altri
 * hanno 1: un errore di cento volte, che falsa rischio e netto atteso di due
 * ordini di grandezza senza che nessuna riga diventi rossa.
 *
 * Sono AVVISI, mai blocchi. I broker esotici esistono, le specifiche cambiano, e
 * un gestionale che si rifiuta di salvare un numero vero è peggio di uno che
 * salva un numero sbagliato segnalandolo. Il criterio è: dire quello che si sa,
 * con il perché, e lasciare decidere.
 *
 * Tre livelli, in ordine di forza:
 *
 *   1. quello che il numero dice di sé          (point contro digits)
 *   2. quello che il simbolo è                  (XAUUSD: un punto vale 1 per lotto)
 *   3. quello che dicono gli altri server       (lo stesso simbolo, altrove)
 *
 * Il terzo è il più utile e il meno assumente: XAUUSD è XAUUSD, e se un server
 * dice cento volte meno di tutti gli altri, uno dei due sbaglia.
 */
import { EXPECTED_CONTRACT_SIZE } from "./canonical-symbols.js";
const n = (v) => {
    if (v === null || v === undefined || v === "")
        return null;
    const x = Number(v);
    return Number.isFinite(x) ? x : null;
};
/** Scarto di scala: 2 se uno è il doppio dell'altro, 100 se è cento volte. */
const ratio = (a, b) => (a > b ? a / b : b / a);
/**
 * Simboli di cui sappiamo il valore punto per misura, non per assunzione.
 * XAUUSD: contratto da 100 once, quindi un punto su un lotto vale 100 volte il
 * punto — 1 con `point` 0,01. È il numero su cui poggiano i test dei lotti.
 *
 * Dalla tabella dei canonici, solo le voci MISURATE: quelle DICHIARATE (il
 * forex) servono alla scelta automatica, e questo controllo confronta il
 * valore punto — in valuta di deposito — col contratto, che su una coppia in
 * JPY differisce per il cambio e darebbe un avviso falso.
 */
const CONTRACT_SIZE = Object.fromEntries(Object.entries(EXPECTED_CONTRACT_SIZE)
    .filter(([, v]) => v?.origin === "MEASURED")
    .map(([k, v]) => [k, v.size]));
/** Oltre questo scarto il numero non è "diverso": è di un'altra grandezza. */
const SCALE_TOLERANCE = 10;
/** Sotto questo passo la gamba broker perde metà del netto: vedi lotStepWarning. */
const COARSE_LOT_STEP = 0.1;
export function symbolSpecWarnings(spec, ctx = {}) {
    const out = [];
    const digits = n(spec.digits);
    const point = n(spec.point);
    const vpp = n(spec.valuePerPoint);
    const lotStep = n(spec.lotStep);
    const minLot = n(spec.minLot);
    const maxLot = n(spec.maxLot);
    const canonical = spec.canonical?.trim().toUpperCase();
    // 1. Il point è la cifra meno significativa del prezzo: con 2 digits è 0,01.
    if (digits !== null && point !== null && point > 0) {
        const atteso = Math.pow(10, -digits);
        if (ratio(point, atteso) > 1.0001) {
            out.push({
                code: "POINT_DIGITS",
                field: "point",
                message: `Point ${fmt(point)}, con ${digits} digits atteso ${fmt(atteso)}.`,
            });
        }
    }
    // 2. Quello che il simbolo è, dove lo sappiamo per misura.
    const contract = canonical ? CONTRACT_SIZE[canonical] : undefined;
    if (contract && point !== null && point > 0 && vpp !== null && vpp > 0) {
        const atteso = contract * point;
        if (ratio(vpp, atteso) > SCALE_TOLERANCE) {
            out.push({
                code: "VALUE_PER_POINT_SYMBOL",
                field: "valuePerPoint",
                // Il contratto che quel valore punto implica: valore punto / point.
                message: `Contratto ${fmt(vpp / point)}, atteso ${fmt(contract)}: probabilmente è uno strumento diverso.`,
            });
        }
    }
    // 3. Lo stesso simbolo sugli altri server. Nessuna assunzione: solo il
    //    confronto fra pari, che è il modo in cui l'errore si nota davvero.
    const peers = (ctx.peers ?? []).map(n).filter((x) => x !== null && x > 0);
    if (vpp !== null && vpp > 0 && peers.length > 0) {
        const mediano = median(peers);
        if (ratio(vpp, mediano) > SCALE_TOLERANCE) {
            out.push({
                code: "VALUE_PER_POINT_PEERS",
                field: "valuePerPoint",
                message: `Valore punto ${fmt(vpp)}, sugli altri server ${fmt(mediano)}.`,
            });
        }
    }
    // 4. Il passo del lotto: fuori da questo intervallo non è un passo, è un refuso.
    if (lotStep !== null && (lotStep <= 0 || lotStep > 1)) {
        out.push({
            code: "LOT_STEP_RANGE",
            field: "lotStep",
            message: `Lot step ${fmt(lotStep)}, atteso 0,01 o 0,1.`,
        });
    }
    // 5. Il lotto minimo deve stare sul passo, altrimenti il primo lotto
    //    ordinabile non è quello che il gestionale calcola.
    if (lotStep !== null && lotStep > 0 && minLot !== null && minLot > 0) {
        const resto = Math.abs(minLot / lotStep - Math.round(minLot / lotStep));
        if (resto > 1e-6) {
            out.push({
                code: "MIN_LOT_STEP",
                field: "minLot",
                message: `Lotto minimo ${fmt(minLot)}, non multiplo del lot step ${fmt(lotStep)}.`,
            });
        }
    }
    if (minLot !== null && maxLot !== null && maxLot > 0 && maxLot < minLot) {
        out.push({
            code: "MAX_LOT",
            field: "maxLot",
            message: `Lotto massimo ${fmt(maxLot)}, sotto il minimo ${fmt(minLot)}.`,
        });
    }
    // 6. Non è un errore, è il criterio di scelta del broker: con passo 0,1 la
    //    gamba broker perde metà del netto sull'arrotondamento.
    if (ctx.serverKind === "BROKER" && lotStep !== null && lotStep >= COARSE_LOT_STEP) {
        out.push({
            code: "BROKER_LOT_STEP",
            field: "lotStep",
            message: `Lot step ${fmt(lotStep)} sulla gamba broker, atteso 0,01.`,
        });
    }
    return out;
}
/**
 * Gli avvisi da MOSTRARE: uno per campo. Due avvisi sullo stesso campo dicono
 * la stessa cosa — quel numero va guardato — e resta il primo, nell'ordine dei
 * controlli qui sopra (il simbolo prima dei pari, la scala prima del broker).
 * I controlli non cambiano: `symbolSpecWarnings` li restituisce tutti.
 */
export function distinctSymbolWarnings(warnings) {
    const visti = new Set();
    return warnings.filter((w) => (visti.has(w.field) ? false : (visti.add(w.field), true)));
}
function median(xs) {
    const s = [...xs].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}
/** Numeri leggibili in italiano, senza zeri di coda. */
function fmt(x) {
    return String(Number(x.toPrecision(10))).replace(".", ",");
}
//# sourceMappingURL=symbol-spec.js.map